import React, { useState, useEffect } from 'react';
import { ArrowRight, Waves, Bike, Building2, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { heroData, stats } from '../mock';
import { carouselAPI } from '../utils/api';

export const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselImages, setCarouselImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default fallback images
  const defaultImages = [
    {
      id: '1',
      url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920',
      title: 'St. Lawrence River Waterfront',
      order: 0
    },
    {
      id: '2',
      url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920',
      title: 'Natural Beauty',
      order: 1
    },
    {
      id: '3',
      url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920',
      title: 'Active Outdoor Lifestyle',
      order: 2
    }
  ];

  // Fetch carousel images from backend
  useEffect(() => {
    const fetchCarouselImages = async () => {
      try {
        const response = await carouselAPI.getAll();
        if (response.data && response.data.length > 0) {
          setCarouselImages(response.data);
        } else {
          setCarouselImages(defaultImages);
        }
      } catch (error) {
        console.error('Error fetching carousel images:', error);
        setCarouselImages(defaultImages);
      } finally {
        setLoading(false);
      }
    };

    fetchCarouselImages();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (carouselImages.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const nextImage = () => {
    if (carouselImages.length === 0) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    if (carouselImages.length === 0) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
    );
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Use current image or first default
  const currentImages = carouselImages.length > 0 ? carouselImages : defaultImages;

  return (
    <section id="home" className="relative pt-24 pb-20 overflow-hidden min-h-[90vh] bg-slate-900">
      {/* Carousel Background - using position fixed-like approach with will-change for GPU acceleration */}
      <div 
        className="absolute top-0 left-0 w-full h-full overflow-hidden"
        style={{ 
          zIndex: 0,
          transform: 'translateZ(0)', // Force GPU layer
          willChange: 'transform',
        }}
      >
        {currentImages.map((image, index) => (
          <div
            key={image.id || index}
            className={`absolute top-0 left-0 w-full h-full transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ 
              transform: 'translateZ(0)',
            }}
          >
            <img
              src={image.url}
              alt={image.title || 'Cornwall'}
              className="w-full h-full object-cover"
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              loading={index === 0 ? 'eager' : 'lazy'}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920';
              }}
            />
            {/* Dark overlay for better text readability */}
            <div 
              className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-transparent"
            ></div>
          </div>
        ))}
      </div>

      {/* Carousel Navigation */}
      {currentImages.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all z-20"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all z-20"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          {/* Carousel Indicators */}
          <div className="absolute bottom-32 left-1/2 -translate-x-1/2 flex space-x-2 z-20">
            {currentImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'bg-white w-8'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/30 shadow-sm">
              <Waves className="w-4 h-4 text-white" />
              <span className="text-sm font-medium text-white">Life on the St. Lawrence</span>
            </div>

            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight drop-shadow-lg">
                {heroData.title}
              </h1>
              <p className="text-xl text-white/90 leading-relaxed max-w-xl drop-shadow-md">
                {heroData.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={() => scrollToSection('benefits')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all text-lg px-8 py-6"
              >
                Explore Benefits
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white/20 backdrop-blur-md rounded-xl p-4 border border-white/30 shadow-lg">
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-white/80 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Feature Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-slate-200 hover:border-emerald-200 transform hover:-translate-y-1">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                  <Waves className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Waterfront Living</h3>
                <p className="text-sm text-slate-600">45km of scenic trails along the majestic St. Lawrence River</p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-slate-200 hover:border-blue-200 transform hover:-translate-y-1">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Business Hub</h3>
                <p className="text-sm text-slate-600">Growing economy with strategic location and low costs</p>
              </div>
            </div>

            <div className="space-y-4 mt-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-slate-200 hover:border-teal-200 transform hover:-translate-y-1">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                  <Bike className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Active Lifestyle</h3>
                <p className="text-sm text-slate-600">Biking, boating, and endless outdoor adventures await</p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-slate-200 hover:border-emerald-200 transform hover:-translate-y-1">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Perfect Location</h3>
                <p className="text-sm text-slate-600">Just 1 hour to Montreal and Ottawa - best of all worlds</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
